"use client";

import { Calendar, MessageSquareText, MousePointer2, Zap } from "lucide-react";
import { Card } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function BrassValveTagsFormats() {
  const intro = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const pillars = [{
    icon: Zap,
    title: "Precision Engineering",
    description: "Our industrial-grade brass valve tags are manufactured to exact specifications, ensuring long-term durability in harsh environments and high-pressure systems.",
    illustration: "cursors"
  }, {
    icon: MessageSquareText,
    title: "Customized Formats",
    description: "We offer a wide range of shapes, sizes, and engraving options to match your specific piping identification requirements and regulatory compliance standards.",
    illustration: "thread"
  }, {
    icon: Calendar,
    title: "Rapid Turnaround",
    description: "Streamline your project timeline with our efficient production process, delivering high-quality identification solutions exactly when your site installation demands them.",
    illustration: "timeline"
  }];
  return <section data-section-id="74" id="brass-valve-tags-formats" className="bg-background text-foreground py-24 md:py-32 lg:py-[140px]">
      <div className="mx-auto w-full max-w-7xl px-6 lg:px-8">
        {}
        <div ref={intro.ref} className={`mx-auto max-w-[720px] text-center transition-all duration-700 ease-out ${intro.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="inline-flex items-center gap-2.5">
            <span className="inline-block size-1.5 rounded-md bg-chart-2" />
            <span className="font-mono text-xs font-medium uppercase tracking-widest text-muted-foreground">
              Valve Identification Solutions
            </span>
          </div>
          <h2 className="font-default mt-5 text-4xl font-semibold leading-[1.05] tracking-tight text-foreground md:text-5xl lg:text-[52px]">
            Engineered for clarity and longevity.
          </h2>
          <p className="mt-6 text-base leading-relaxed text-muted-foreground md:text-lg">
            From heavy-duty brass tags to custom-engraved identification plates, we provide the essential components for professional facility management and safety compliance.
          </p>
        </div>

        {}
        <div className="mt-16 grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-6 lg:mt-[72px] lg:grid-cols-3">
          {pillars.map((pillar, i) => {
          const Icon = pillar.icon;
          return <PillarCard key={pillar.title} data-index={i} index={i} icon={<Icon className="size-6 text-primary" strokeWidth={2} />} title={pillar.title} description={pillar.description} illustration={pillar.illustration} lastInOddGrid={i === 2} />;
        })}
        </div>
      </div>
    </section>;
}
function PillarCard({
  index,
  icon,
  title,
  description,
  illustration,
  lastInOddGrid,
  ...rest
}: {
  index: number;
  icon: React.ReactNode;
  title: string;
  description: string;
  illustration: string;
  lastInOddGrid?: boolean;
} & React.HTMLAttributes<HTMLDivElement>) {
  const reveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <div {...rest} ref={reveal.ref} className={`group h-full transition-all duration-700 ease-out ${reveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"} ${lastInOddGrid ? "sm:col-span-2 lg:col-span-1" : ""}`} style={{
    transitionDelay: `${index * 120}ms`
  }}>
      <Card className="relative flex h-full flex-col justify-between gap-10 border border-border bg-card p-8 shadow-md transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-lg md:p-10">
        {}
        <div>
          <div className="inline-flex size-14 items-center justify-center rounded-lg bg-primary/10 transition-colors duration-300 group-hover:bg-primary/15">
            {icon}
          </div>

          <h3 className="font-default mt-6 text-[22px] font-semibold leading-tight tracking-tight text-card-foreground">
            {title}
          </h3>

          <p className="mt-3 text-base leading-relaxed text-muted-foreground">
            {description}
          </p>
        </div>

        {}
        <div className="pt-4">
          {illustration === "cursors" && <CursorsIllustration />}
          {illustration === "thread" && <ThreadIllustration />}
          {illustration === "timeline" && <TimelineIllustration />}
        </div>
      </Card>
    </div>;
}
function CursorsIllustration() {
  return <div className="relative h-16 w-full">
      {}
      <span className="absolute left-3 top-3 h-px w-10 rounded-md bg-primary/20" />
      <span className="absolute left-14 top-7 h-px w-8 rounded-md bg-chart-4/30" />
      <span className="absolute left-28 top-11 h-px w-10 rounded-md bg-chart-2/40" />

      <MousePointer2 className="absolute left-1 top-1 size-6 -rotate-12 text-primary drop-shadow-sm" fill="currentColor" strokeWidth={1.5} />
      <MousePointer2 className="absolute left-10 top-5 size-6 rotate-6 text-chart-4 drop-shadow-sm" fill="currentColor" strokeWidth={1.5} />
      <MousePointer2 className="absolute left-24 top-9 size-6 -rotate-3 text-chart-2 drop-shadow-sm" fill="currentColor" strokeWidth={1.5} />
    </div>;
}
function ThreadIllustration() {
  const items = [{
    color: "bg-primary",
    w: "w-24"
  }, {
    color: "bg-chart-4",
    w: "w-32"
  }, {
    color: "bg-chart-2",
    w: "w-20"
  }];
  return <div className="flex flex-col gap-2">
      {items.map((item, i) => <div key={i} data-index={i} className="flex items-center gap-2.5">
          <span className={`inline-block size-5 shrink-0 rounded-lg ${item.color} opacity-80`} />
          <span className={`block h-2 ${item.w} rounded-md bg-muted`} aria-hidden />
        </div>)}
    </div>;
}
function TimelineIllustration() {
  const bars = [{
    color: "bg-primary",
    w: "w-3/4",
    ml: "ml-0"
  }, {
    color: "bg-chart-4",
    w: "w-1/2",
    ml: "ml-8"
  }, {
    color: "bg-chart-2",
    w: "w-2/3",
    ml: "ml-4"
  }];
  return <div className="flex flex-col gap-2">
      {bars.map((bar, i) => <div key={i} data-index={i} className={`${bar.ml}`}>
          <span className={`block h-2.5 ${bar.w} rounded-md ${bar.color} opacity-85`} />
        </div>)}
    </div>;
}